import type {
  FormStep,
  RadioInputStepInput,
  RenderableFormItem,
} from "./form-types";

import { isRadioInput } from "./is-radio-input";

/**
 * The auto-advance "next" formButton, as opposed to one that submits the form.
 * LeadFormButtonInput treats every other buttonType as a submit button, so this
 * is the single place that rule is expressed.
 */
function isNextFormButton(input: RenderableFormItem): boolean {
  return input._type === "formButton" && input.buttonType === "next";
}

/**
 * True when the form has at least one step with a visible postalCode standardInput.
 * Used to gate postal hidden inputs and initial postal context.
 */
export function stepsHavePostalCodeInput(steps: FormStep[]): boolean {
  return steps.some(
    (step) =>
      step._type === "step" &&
      step.inputs?.some(
        (input) =>
          input._type === "standardInput" && input.inputType === "postalCode",
      ),
  );
}

/**
 * Filters step inputs for display: removes the auto-advance "next" formButton when
 * the step has an auto-advance input (so we don't show both the radio and the button).
 */
export function filterStepInputs(
  stepInputs: RenderableFormItem[] | null | undefined,
  hasAutoAdvanceInput: boolean,
): RenderableFormItem[] {
  const inputs = stepInputs ?? [];
  // No mutation downstream — return the original array to avoid an unnecessary allocation.
  if (!hasAutoAdvanceInput) {
    return inputs;
  }
  return inputs.filter((input) => !isNextFormButton(input));
}

/**
 * Visible inputs from a step for rendering.
 */
export function getVisibleInputs(
  stepInputs: RenderableFormItem[] | null | undefined,
  hasAutoAdvanceInput: boolean,
): RenderableFormItem[] {
  return filterStepInputs(stepInputs, hasAutoAdvanceInput);
}

/**
 * True when the given visible inputs include a postalCode standardInput.
 */
export function stepInputsRequirePostalLocation(
  visibleInputs: RenderableFormItem[],
): boolean {
  return visibleInputs.some(
    (input) =>
      input._type === "standardInput" && input.inputType === "postalCode",
  );
}

/**
 * Gates the browser's implicit submission to the one step that owns a submit
 * button — see LeadForm's handleSubmit.
 */
export function stepHasSubmitButton(step: FormStep): boolean {
  const inputs: RenderableFormItem[] = step.inputs ?? [];
  return inputs.some(
    (input) => input._type === "formButton" && !isNextFormButton(input),
  );
}

/**
 * Finds the auto-advance radio input in a step.
 */
export function getAutoAdvanceRadioInput(
  step: FormStep,
): RadioInputStepInput | undefined {
  return step.inputs?.find(
    (input): input is RadioInputStepInput =>
      isRadioInput(input) && Boolean(input.autoAdvance),
  );
}
